/**
 * The receipt tape — ONE renderer, shared by the cashier (checkout, releasing,
 * reprint) and the admin sales drill-down.
 *
 * It lived in `apps/cashier/src/components/receipt.tsx` until Admin → Reports
 * grew a row-click receipt. Two apps drawing the same customer document from two
 * components is how a reprint starts disagreeing with the original tape, so it
 * moved here rather than being copied.
 *
 * The `.receipt-tape` element is the only thing that survives `@media print`;
 * each consuming app supplies those rules in its own stylesheet.
 */
import { useEffect, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { orderCustomFeeLines, resolveReceiptFields, type ReceiptData } from '@mlm/shared';
import { Button } from './button';
import { Card, CardContent, CardHeader, CardTitle } from './card';

/**
 * Date and time as two rows rather than one locale string: at 80mm a combined
 * stamp wraps unpredictably, and the two are read for different reasons (which
 * day for a return window, which minute for a drawer dispute).
 */
function fmtDate(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString();
}
function fmtTime(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? '—'
    : d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

/**
 * A missing/garbled amount prints `—`, never `₱0.00`. The old formatter
 * coerced null/''/undefined straight to `₱0.00`, which is indistinguishable
 * from a genuine zero-value sale — a cashier escalates a dash, not a zero.
 */
function fmt(v: string | null | undefined): string {
  const n = Number(v ?? NaN);
  return Number.isFinite(n) ? n.toFixed(2) : '—';
}
const peso = (v: string | null | undefined) => `₱${fmt(v)}`;

export function Receipt({
  data,
  onNew,
  onClose,
  newLabel = 'New sale',
}: {
  data: ReceiptData;
  onNew?: () => void;
  onClose?: () => void;
  newLabel?: string;
}) {
  const queued = data.status === 'to_release';
  const fields = resolveReceiptFields(data.fields);

  return (
    <Card className="max-w-md mx-auto">
      <CardHeader className="no-print">
        <CardTitle>{queued ? 'Payment received' : 'Sale complete'}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="receipt-tape font-mono text-sm space-y-1">
          {data.header && (
            <div className="text-center space-y-0.5 pb-2 border-b border-dashed">
              {data.header.companyName && (
                <div className="font-semibold text-base">{data.header.companyName}</div>
              )}
              {data.header.address && <div className="text-xs">{data.header.address}</div>}
              {data.header.phone && <div className="text-xs">{data.header.phone}</div>}
              {data.header.tin && <div className="text-xs">TIN: {data.header.tin}</div>}
            </div>
          )}

          {/* Transaction meta. The order code is unconditional — it's the one
              thing a customer quotes back — everything else is per-branch
              configurable in Admin → Branch → POS & Receipt. */}
          <div className="flex justify-between pt-1">
            <span>Order</span>
            <span>{data.orderCode}</span>
          </div>
          {fields.showBranchName && data.branchName && (
            <div className="flex justify-between">
              <span>Branch</span>
              <span className="text-right break-words">{data.branchName}</span>
            </div>
          )}
          {fields.showDateTime && data.createdAt && (
            <>
              <div className="flex justify-between">
                <span>Date</span>
                <span>{fmtDate(data.createdAt)}</span>
              </div>
              <div className="flex justify-between">
                <span>Time</span>
                <span>{fmtTime(data.createdAt)}</span>
              </div>
            </>
          )}
          {fields.showMemberName && data.memberName && (
            <div className="flex justify-between">
              <span>Customer</span>
              <span className="text-right break-words">{data.memberName}</span>
            </div>
          )}
          {fields.showCashierName && data.cashierName && (
            <div className="flex justify-between">
              <span>Cashier</span>
              <span className="text-right break-words">{data.cashierName}</span>
            </div>
          )}

          {/* Sold lines. No <table> — at 80mm a table's column widths collapse
              unpredictably, so each line is a wrapping name followed by a
              qty × price / line-total row. */}
          <div className="border-t border-dashed pt-1 mt-1 space-y-1">
            {data.items.length === 0 ? (
              <div className="text-[10px] text-muted-foreground text-center">
                (no line items recorded)
              </div>
            ) : (
              data.items.map((it, i) => (
                <div key={`${it.name}-${it.variantName ?? ''}-${i}`}>
                  <div className="break-words leading-tight">{it.name}</div>
                  {it.variantName && (
                    <div className="pl-2 text-[10px] text-muted-foreground leading-tight">
                      {it.variantName}
                    </div>
                  )}
                  <div className="flex justify-between pl-2">
                    <span>
                      {it.qty} × {fmt(it.unitPrice)}
                    </span>
                    <span className="tabular-nums">
                      {Number(it.lineTotal) === 0 ? 'FREE' : fmt(it.lineTotal)}
                    </span>
                  </div>
                </div>
              ))
            )}
          </div>

          <div className="flex justify-between border-t border-dashed pt-1">
            <span>Subtotal</span>
            <span>{peso(data.subtotal)}</span>
          </div>
          {Number(data.discountTotal) > 0 && (
            <div className="flex justify-between">
              <span>Discount</span>
              <span>−{peso(data.discountTotal)}</span>
            </div>
          )}
          {/* Every charge printed on its own line — a customer must be able to
              see what each bit of extra money was for. Always printed when
              charged; not flag-gated. `orderCustomFeeLines` is the ONE reader,
              so a pre-breakdown order still prints its single fee here. */}
          {orderCustomFeeLines(data).map((f, i) => (
            <div key={i} className="flex justify-between">
              <span className="break-words">{f.label}</span>
              <span>{peso(f.amount)}</span>
            </div>
          ))}
          {/* A genuinely ₱0 sale (e.g. a ₱0-priced product) is legitimate but
              looks identical to a broken tape — say so on the paper. */}
          {data.items.length > 0 && Number(data.total) === 0 && (
            <div className="text-center text-[10px] font-semibold pt-1">** ZERO-VALUE SALE **</div>
          )}
          <div className="flex justify-between font-semibold border-t border-dashed pt-1">
            <span>Total</span>
            <span>{peso(data.total)}</span>
          </div>

          {data.payment && (
            <>
              <div className="flex justify-between pt-1">
                <span className="capitalize">
                  {data.payment.methodLabel || data.payment.method}
                </span>
                <span>{peso(data.payment.amountTendered)}</span>
              </div>
              {Number(data.payment.changeDue) > 0 && (
                <div className="flex justify-between font-semibold">
                  <span>Change</span>
                  <span>{peso(data.payment.changeDue)}</span>
                </div>
              )}
            </>
          )}

          {data.kitCodes.length > 0 && (
            <div className="border-t border-dashed pt-2 mt-1 space-y-1">
              <div className="text-center text-xs font-semibold">ACTIVATION CODES</div>
              {data.kitCodes.map((c) => (
                <div key={c.code} className="flex justify-between">
                  <span className="tracking-widest">{c.code}</span>
                  {/* A withheld PIN prints masked rather than blank, so the row
                      still reads as "a code was issued" on a back-office copy. */}
                  <span>PIN {c.pin ?? '••••••'}</span>
                </div>
              ))}
              <div className="text-center text-[10px] text-muted-foreground">
                Keep this slip — codes activate your slot.
              </div>
            </div>
          )}

          {queued && (
            <div className="border-t border-dashed pt-2 mt-1 text-center text-xs">
              ★ Claim your items at the Releasing area ★
            </div>
          )}

          {fields.showRemarks && data.remarks && (
            <div className="border-t border-dashed pt-2 mt-1 text-[10px] break-words">
              <span className="text-muted-foreground">Remarks: </span>
              {data.remarks}
            </div>
          )}

          {data.header?.footer && (
            <div className="text-center text-[10px] text-muted-foreground pt-2">
              {data.header.footer}
            </div>
          )}
        </div>

        <div className="no-print flex gap-2">
          <Button variant="outline" className="flex-1" onClick={() => window.print()}>
            Print
          </Button>
          {onNew && (
            <Button className="flex-1" onClick={onNew}>
              {newLabel}
            </Button>
          )}
          {onClose && (
            <Button variant="outline" className="flex-1" onClick={onClose}>
              Close
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * Mounts a receipt as a body-level portal so it can be printed on its own.
 *
 * This is NOT cosmetic. The print stylesheets hide the rest of the app with
 * `visibility: hidden`, which conceals elements but leaves them occupying their
 * full height — printing over a long page emitted the tape on page 1 followed by
 * blank pages padded out by the invisible content (the cashier's 200-row
 * Transaction History produced ~8, and the admin Reports table is exactly that
 * shape). As a direct child of <body> the tape's siblings can instead be
 * collapsed with `display: none`, which removes them from the layout.
 *
 * Both apps' stylesheets carry the matching `body.printing-receipt > *:not(.print-root)`
 * rule. Do NOT render `<Receipt>` inside a Dialog and expect printing to work:
 * Radix portals its content to <body> too, so the dialog would be one of the
 * siblings this collapses.
 */
export function ReceiptPrintPortal({
  data,
  caption,
  onClose,
  children,
}: {
  data: ReceiptData;
  /** Small line above the tape, e.g. "Reprint of sale MB0001". */
  caption?: ReactNode;
  onClose?: () => void;
  children?: ReactNode;
}) {
  // Tag <body> while the tape is mounted so the print stylesheet can collapse
  // everything else. Cleaned up on unmount — a stale class would blank the app
  // on the NEXT print from any other page.
  useEffect(() => {
    document.body.classList.add('printing-receipt');
    return () => document.body.classList.remove('printing-receipt');
  }, []);

  return createPortal(
    <div className="print-root fixed inset-0 z-50 overflow-y-auto bg-background p-4">
      {caption && (
        <p className="no-print mx-auto mb-2 max-w-md text-center text-xs text-muted-foreground">
          {caption}
        </p>
      )}
      <Receipt data={data} onClose={onClose} />
      {children}
    </div>,
    document.body,
  );
}
